// @ts-nocheck
import { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Settings, 
  CreditCard, 
  Shield, 
  Bell, 
  Users,
  HelpCircle
} from 'lucide-react';

// Import settings components
import { GeneralSettings } from './settings/general-settings';
import { BillingSubscriptions } from './settings/billing-subscriptions';
import { SecurityPrivacy } from './settings/security-privacy';
import { Notifications } from './settings/notifications';
import { UserManagement } from './user-management';
// ResendDiagnostic tab hidden — diagnostics available via edge function /debug-env
import { hasPermission, getRoleDisplayName, type UserRole } from '@product-radar/shared';

interface SettingsMenuItem {
  id: string;
  label: string;
  icon: any;
  description: string;
  badge?: string;
  requiredPermission: string;
}

const settingsMenuItems: SettingsMenuItem[] = [
  { 
    id: 'general', 
    label: 'General', 
    icon: Settings,
    description: 'Workspace settings and preferences',
    requiredPermission: 'canAccessGeneralSettings'
  },
  { 
    id: 'billing', 
    label: 'Billing & Subscriptions', 
    icon: CreditCard,
    description: 'Manage plans, usage, and billing',
    requiredPermission: 'canAccessBillingSettings'
  },
  { 
    id: 'users', 
    label: 'User Management', 
    icon: Users,
    description: 'Manage team members and permissions',
    requiredPermission: 'canAccessUserManagement'
  },
  { 
    id: 'security', 
    label: 'Security', 
    icon: Shield,
    description: 'Security settings and access controls',
    requiredPermission: 'canAccessSecuritySettings'
  },
  { 
    id: 'notifications', 
    label: 'Notifications', 
    icon: Bell,
    description: 'Configure notification preferences',
    requiredPermission: 'canAccessNotifications'
  },
];

interface SettingsNavigationProps {
  activeSettingsView: string;
  setActiveSettingsView: (view: string) => void;
  userProfile?: any;
  checkoutResult?: 'success' | 'canceled' | null;
  onCheckoutResultClose?: () => void;
}

export function SettingsNavigation({ activeSettingsView, setActiveSettingsView, userProfile, checkoutResult, onCheckoutResultClose }: SettingsNavigationProps) {
  // Check permissions for each item
  const userRole = userProfile?.role as UserRole;
  const roleDisplayName = getRoleDisplayName(userRole || 'viewer');

  const renderSettingsContent = () => {
    // Check if user has permission to view this content
    const currentItem = settingsMenuItems.find(item => item.id === activeSettingsView);
    const hasAccess = currentItem ? hasPermission(userRole, currentItem.requiredPermission as any) : false;
    
    if (!hasAccess) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Access Restricted</h3>
            <p className="text-muted-foreground">
              You don't have permission to access this section.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              You are a {roleDisplayName}
            </p>
          </div>
        </div>
      );
    }

    switch (activeSettingsView) {
      case 'general':
        return <GeneralSettings />;
      case 'billing':
        return (
          <BillingSubscriptions
            checkoutResult={checkoutResult ?? null}
            onCheckoutResultClose={onCheckoutResultClose}
          />
        );
      case 'users':
        return <UserManagement />;
      case 'security':
        return <SecurityPrivacy />;
      case 'notifications':
        return <Notifications />;
      default:
        return <GeneralSettings />;
    }
  };

  return (
    <div className="flex h-screen">
      {/* Settings Sidebar */}
      <div className="w-80 border-r border-border bg-muted/30 relative">
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-1">Settings</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Manage your workspace configuration
          </p>
          
          <nav className="space-y-1">
              {settingsMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSettingsView === item.id;
                const hasAccess = hasPermission(userRole, item.requiredPermission as any);
                
                const buttonContent = (
                  <Button
                    key={item.id}
                    variant={isActive ? "secondary" : "ghost"}
                    className={`w-full justify-start h-auto p-3 ${
                      !hasAccess ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    onClick={() => hasAccess && setActiveSettingsView(item.id)}
                    disabled={!hasAccess}
                  >
                    <Icon className="h-4 w-4 mr-3 flex-shrink-0" />
                    <div className="flex-1 text-left">
                      <div className="flex items-center justify-between">
                        <span className={`font-medium ${!hasAccess ? 'text-muted-foreground' : ''}`}>
                          {item.label}
                        </span>
                        {item.badge && hasAccess && (
                          <Badge variant="secondary" className="ml-2 text-xs">
                            {item.badge}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {item.description}
                      </p>
                    </div>
                  </Button>
                );

                return <div key={item.id}>{buttonContent}</div>;
              })}
          </nav>
        </div>
        
        {/* Help Section */}
        <div className="absolute bottom-6 left-6 right-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <HelpCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <h4 className="font-medium text-sm">Need Help?</h4>
                  <p className="text-xs text-muted-foreground mb-3">
                    Check our documentation or contact support
                  </p>
                  <Button variant="outline" size="sm" className="w-full" asChild>
                    <a
                      href="https://productradar.co/docs/user-guide"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Documentation
                    </a>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Settings Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6">
          {renderSettingsContent()}
        </div>
      </div>
    </div>
  );
}